<template>
  <FormLayout
    :is-loading="!unitMapData && isLoading"
    :title="unitMapData ? unitMapData.name : ''"
    :overline="overlineText"
    :description="description"
    @on-dismiss="handleDismiss"
  >
    <vs-wrapper>
      <vs-wrapper space="small">
        <UnitMinimap v-if="hasLocation" :unit="unitMapData" :center="unitLocation" />
        <vs-text v-if="!hasLocation" center type="subdued"
          >Resource location not available yet
        </vs-text>
      </vs-wrapper>

      <v-row>
        <v-col class="shrink pr-2">
          <v-icon large color="grey darken-2">{{ unitStatusIcon.name }}</v-icon>
        </v-col>

        <v-col>
          <vs-text type="overline">Status</vs-text>
          <vs-text>{{ unitStatusIcon.helpText }}</vs-text>
        </v-col>
      </v-row>

      <v-row>
        <v-col class="shrink pr-2">
          <v-icon large color="grey darken-2">navigation</v-icon>
        </v-col>

        <v-col>
          <vs-text type="overline">Last GPS update by</vs-text>
          <vs-text>{{ latestUpdatedInfo || 'No updates yet' }}</vs-text>
        </v-col>
      </v-row>

      <v-row>
        <v-col class="shrink pr-2">
          <v-icon large color="grey darken-2">account_circle</v-icon>
        </v-col>

        <v-col>
          <vs-text type="overline">Operated by</vs-text>
          <vs-text>{{ operatorName }}</vs-text>
          <v-row v-if="hasOperatorData" no-gutters align="center">
            <v-col class="shrink mr-2">
              <v-icon x-small :color="onDutyColor">mdi-checkbox-blank-circle </v-icon>
            </v-col>
            <v-col>
              <vs-text type="subdued">{{ onDutySubtext }}</vs-text>
            </v-col>
          </v-row>
        </v-col>
      </v-row>

      <v-row>
        <v-col>
          <vs-button
            full-width
            label="See in Map"
            color="blue darken-1"
            :disabled="!hasLocation"
            :to="{
              name: 'fvnext-info-panel',
              params: { id: id, source: 'resources' },
            }"
          />
        </v-col>
      </v-row>

      <v-row v-if="canPartiallyModifyResource && unitMapData && canEditResource">
        <v-col>
          <vs-button
            data-test="save"
            full-width
            :loading="saving"
            type="secondary"
            :label="statusChangeLabel"
            @click="handleStatusChange"
          />
        </v-col>
        <v-col>
          <vs-button
            full-width
            :loading="saving"
            type="secondary"
            label="Edit Resource"
            :to="{
              name: editViewRouteName,
            }"
          />
        </v-col>
      </v-row>
    </vs-wrapper>

    <UnitOfflineDialog
      v-if="showOfflineDialog"
      :unit-name="unitMapData?.name"
      :loading="saving"
      @update-status="setUnitStatusToOffline"
      @dismiss="closeOfflineDialog"
    />
  </FormLayout>
</template>

<script setup lang="ts">
import { computed, getCurrentInstance, ref, toRef } from 'vue'
import { useRouter } from 'vue-router/composables'
import { useStore } from 'vuex-composition-helpers'
import moment from 'moment'
import { ACTIVE, OFFLINE, STANDBY } from '@/models/unit_number'
import { useResourceLocationQuery, useResourceQuery } from '@/api/unit_numbers'
import { formatDateTime } from '@/utils/date-formatting'
import UnitOfflineDialog from '@/components/common/UnitOfflineDialog.vue'
import FormLayout from '@/components/common/FormLayout.vue'
import UnitMinimap from '@/components/common/unit-number-panel/UnitMinimap.vue'
import VsButton from '@/components/vision/VsButton.vue'
import Division from '@/models/division'
import Company from '@/models/company'
import { BusinessUnit } from '@/models/business-unit'
import User from '@/models/user'
import VsWrapper from '@/components/vision/VsWrapper.vue'
import VsText from '@/components/vision/VsText.vue'

const props = defineProps<{
  id: string | number
  parentRouteName: string
  editViewRouteName: string
}>()

const store = useStore()
const router = useRouter()
const instance = getCurrentInstance()

const saving = computed(() => store.state.unitNumbers.saving)

const showOfflineDialog = ref(false)

const resourceId = toRef(props, 'id')
const { data: unitMapData, isLoading, refetch: refetchResource } = useResourceQuery(resourceId)

const { data: latestLocation } = useResourceLocationQuery(resourceId)

const getUserBySid = computed<(sid: string) => User>(() => store.getters['users/getBySid'])

const canPartiallyModifyResource = computed<boolean>(
  () => store.getters['permissions/canPartiallyModifyResource'],
)
const getBusinessUnitById = computed<(id?: string) => BusinessUnit>(
  () => store.getters['businessUnits/getBusinessUnitById'],
)
const resourceOwner = computed(() => getBusinessUnitById.value(unitMapData.value?.ownerId))
const currentUserOrgId = computed<string>(() => store.getters['permissions/getOrganizationId'])
const canEditResource = computed(
  () =>
    resourceOwner.value?.id === currentUserOrgId.value ||
    resourceOwner.value?.ownerId === currentUserOrgId.value,
)

const getDivisionById = computed<(id: number | null) => Division>(
  () => store.getters['divisions/getById'],
)
const getCompanyByOrganizationId = computed<(id: string | null) => Company>(
  () => store.getters['companies/getByOrganizationId'],
)

const division = computed(() => getDivisionById.value(unitMapData.value.divisionId))
const company = computed(() =>
  getCompanyByOrganizationId.value(unitMapData.value.rootOrganizationId),
)

const overlineText = computed<string>(() => {
  if (!unitMapData.value) {
    return ''
  }

  const companyName = company.value?.name || ''
  const divisionName = division.value?.name || ''

  if (companyName && divisionName) {
    return `${divisionName}: ${companyName}`
  }

  return divisionName || companyName
})

type UnitStatusIcon = {
  name: string
  helpText: string
}
const unitStatusIcon = computed<UnitStatusIcon>(() => {
  switch (unitMapData.value?.status) {
    case OFFLINE:
      return {
        name: 'mdi-close-circle',
        helpText: 'Offline',
      }
    case STANDBY:
      return {
        name: 'pause_circle_filled',
        helpText: 'Standby',
      }
    case ACTIVE:
      return {
        name: 'play_circle_filled',
        helpText: 'Active',
      }
    default:
      return {
        name: 'help_outline',
        helpText: 'Loading',
      }
  }
})

const latestUpdatedInfo = computed<string | null>(() => {
  if (latestLocation.value && latestLocation.value.userSid) {
    const timestamp = moment.utc(latestLocation.value.timestamp).local()
    const user = getUserBySid.value(latestLocation.value.userSid) || {}
    return `${user.name} - ${timestamp.fromNow()}`
  }
  return null
})

const hasOperatorData = computed<boolean>(() => {
  return unitMapData.value?.operator != null
})

const operatorName = computed<string>(() => {
  const operator = unitMapData.value?.operator
  if (operator && operator.userSid) {
    const user = getUserBySid.value(operator.userSid)
    if (user) {
      return user.name
    }
  }
  return 'No field workers on duty'
})

const onDutyColor = computed<string>(() => {
  if (unitMapData.value?.operator?.onDuty) {
    return 'teal accent-2'
  }
  return 'grey lighten-1'
})

const onDutySubtext = computed<string | null>(() => {
  if (!hasOperatorData.value) return null
  if (unitMapData.value?.operator?.onDuty) {
    return `On duty since ${formatDateTime(
      moment.utc(unitMapData.value?.operator.occurredAt).local(),
    )}`
  } else {
    return `Last on duty ${formatDateTime(
      moment.utc(unitMapData.value?.operator?.occurredAt).local(),
    )}`
  }
})

const statusChangeLabel = computed<string>(() => {
  return unitMapData.value?.status === OFFLINE ? 'Mark as Online' : 'Mark as Offline'
})

const description = computed<string>(() => {
  if (!unitMapData.value) {
    return ''
  }
  return [unitMapData.value?.description, unitMapData.value?.statusDescription]
    .filter(Boolean)
    .join('\n')
})

const hasLocation = computed<boolean>(() => {
  return !!(
    unitMapData.value?.id &&
    latestLocation.value?.longitude &&
    latestLocation.value?.latitude
  )
})

interface Coordinate {
  lat: number
  lng: number
}

const unitLocation = computed<Coordinate | Record<any, any>>(() => {
  if (!latestLocation.value) return {}

  const { latitude: lat, longitude: lng } = latestLocation.value

  return { lat, lng }
})

const setUnitStatus = async ({ id, status }) => {
  // We need to update the resource status first before refetching its data to the frontend
  await store.dispatch('unitNumbers/setUnitStatus', { id, status })
  await refetchResource()
}

const handleStatusChange = () => {
  if (unitMapData.value?.status !== OFFLINE) {
    openOfflineDialog()
  } else {
    setUnitStatus({ id: unitMapData.value?.id, status: STANDBY })
  }
}

const setUnitStatusToOffline = () => {
  setUnitStatus({ id: unitMapData.value?.id, status: OFFLINE })
  closeOfflineDialog()
}

const closeOfflineDialog = () => {
  showOfflineDialog.value = false
}

const openOfflineDialog = () => {
  showOfflineDialog.value = true
}
const handleDismiss = () => {
  if (instance) {
    router.push({ name: props.parentRouteName })
  }
}
</script>
